/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import i18next from 'i18next'
import {initReactI18next, useTranslation} from 'react-i18next'
// English namespace resources loaded at init time.
// For non-English locales, the engine capability loads namespace translations
// from the compiled locale file and calls i18next.addResourceBundle() per
// namespace. See ui/engine/capabilities/I18n/index.ts.
import rawCanvasLmsEn from '@instructure/translations/lib/canvas-lms/en.json'

// en.json was generated with key===value for ~99% of entries (artifacts of
// the old translation pipeline). Those entries must be excluded so i18next
// falls through to the defaultValue supplied in each t() call, which IS
// the correct English text. Keep only entries where value !== key (e.g.
// pluralization forms like key_one / key_other).
const canvasLmsEn = Object.fromEntries(
  Object.entries(rawCanvasLmsEn).map(([ns, nsObj]) => [
    ns,
    typeof nsObj === 'object' && nsObj !== null
      ? Object.fromEntries(Object.entries(nsObj).filter(([k, v]) => v !== k))
      : nsObj,
  ]),
) as typeof rawCanvasLmsEn

let initialized = false

function initI18next(locale?: string) {
  if (initialized) {
    if (locale && locale !== i18next.language) {
      i18next.changeLanguage(locale)
    }
    return
  }

  const lng = locale || document.documentElement.getAttribute('lang') || 'en'

  i18next.use(initReactI18next).init({
    lng,
    fallbackLng: 'en',
    defaultNS: false,
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
      // Use i18next default {{var}} syntax for newly migrated features.
      // Old @canvas/i18n uses %{var} — migrated strings should be updated.
    },
    // initImmediate: false makes init synchronous since we provide all
    // resources inline — no async backend needed.
    initImmediate: false,
    react: {
      useSuspense: false,
      // Re-render components when addResourceBundle() loads translations
      // for a new locale. Without this, components rendered before
      // translations load would show English permanently.
      bindI18nStore: 'added',
    },
    resources: {
      en: canvasLmsEn,
    },
  })

  initialized = true
}

// Auto-initialize on import. The engine capability calls initI18next(locale)
// once ENV.LOCALE is known, which syncs the language if it differs from the
// default derived from the <html lang> attribute.
initI18next()

function loadI18nextTranslations(
  locale: string,
  namespaces: Record<string, Record<string, string>>,
) {
  for (const [ns, translations] of Object.entries(namespaces)) {
    i18next.addResourceBundle(locale, ns, translations, true, true)
  }
}

// WrapperOption mirrors the `wrapper`/`wrappers` option from the old @canvas/i18n
// system: a single template string, an ordered array of templates (index 0 = *…*,
// index 1 = **…**, etc.), or an object keyed by the delimiter string itself
// (e.g. {'*': '<i>$1</i>', '**': '<b>$1</b>'}). Object keys must be pure asterisk
// strings ('*', '**', '***', …); non-asterisk delimiters are not supported.
export type WrapperOption = string | string[] | Record<string, string>

// Process the `wrapper`/`wrappers` option from the old @canvas/i18n system.
// In that system, *text* → wrappers[0].replace('$1', text), **text** → wrappers[1].
// i18next does not support this natively; call this helper after t().
//
// Note: this helper does NOT HTML-escape the result. The i18next config sets
// escapeValue: false and migrated call sites render output via React JSX (which
// escapes text nodes) or dangerouslySetInnerHTML with an explicit sanitizer.
// Callers that rely on the old @canvas/i18n auto-escaping must handle escaping
// themselves before passing the result to the DOM.
export function applyWrappers(text: string, wrapper?: WrapperOption): string {
  if (!wrapper) return text

  // Normalise all three input shapes into a map of asterisk-count → template.
  // Using a map (rather than a flat array) means sparse object forms like
  // {'**': tmpl} correctly match \*\*…\*\* instead of \*…\*.
  const wrapperMap: Record<number, string> = {}
  if (typeof wrapper === 'string') {
    wrapperMap[1] = wrapper
  } else if (Array.isArray(wrapper)) {
    wrapper.forEach((tmpl, i) => {
      wrapperMap[i + 1] = tmpl
    })
  } else {
    // Object keyed by delimiter ('*', '**', …). The key length is the asterisk count.
    Object.entries(wrapper).forEach(([delim, tmpl]) => {
      wrapperMap[delim.length] = tmpl
    })
  }

  // Process longer delimiters first (e.g. ** before *) to avoid partial matches.
  let result = text
  const counts = Object.keys(wrapperMap)
    .map(Number)
    .sort((a, b) => b - a)
  for (const count of counts) {
    const regex = new RegExp(`\\*{${count}}([^*]+)\\*{${count}}`, 'g')
    result = result.replace(regex, (_, p1) => wrapperMap[count].replace('$1', p1))
  }
  return result
}

export {initI18next, loadI18nextTranslations, useTranslation}
export default i18next
